<template>
  <div>
    <button v-if="itemId" :disabled="disabled" :class="$style.itemButton" @click="onClick">
      <i class="icon-message" />
      <span>質問</span>
    </button>
    <button v-else :disabled="disabled" title="メッセージを送る" aria-label="メッセージを送る" :class="$style.shopButton" @click="onClick">
      <i class="icon-message" />
    </button>
    <IModal v-model="isShowMessageDialog" width="600px">
      <p :class="$style.title">メッセージを送る</p>
      <Form :class="$style.form" @submit="onSubmit">
        <section>
          <ITextField v-model="subjectStr" name="件名" title="件名" rules="required|max:200" />
        </section>
        <section>
          <ITextArea v-model="messageStr" name="メッセージ" title="メッセージ" :rows="5" rules="required|max:800" />
        </section>
        <div :class="$style.attach">
          <button @click.prevent="isShowImage = !isShowImage">画像を添付する</button>
        </div>
        <section v-if="isShowImage">
          <div :class="$style.sectionHeader">
            <p>添付画像(任意)</p>
            <IMenu width="276">
              <template #link>
                <QuestionIcon top="3" />
              </template>
              <template #menu>
                [推奨画像サイズ] 横320ピクセル以上<br />
                [対応フォーマット] GIF、JPEG、PNG<br />
                [ファイルサイズ] 10MB以下
              </template>
            </IMenu>
          </div>
          <PhotoUploader v-model="pictures" name="添付画像" />
        </section>
        <div :class="$style.send">
          <IButton type="primary" native-type="submit">送信する</IButton>
        </div>
      </Form>
    </IModal>
  </div>
</template>

<script setup lang="ts">
import { Form } from 'vee-validate';
import { ref } from 'vue';
import { type Picture } from '@/types/photo-uploader';
import { messageSend } from '@/js/lib/api/message';
import { credentials } from '@/js/lib/api/user';
import { useUserStore } from '@/js/lib/store/user';
import { confirm, info, loading } from '@/js/lib/ui';
import { signinSignup } from '@/js/lib/ui/signin-signup';
import PhotoUploader from '@/js/components/common/ui/form/photo-uploader.vue';
import IButton from '@/js/components/molecules/IButton.vue';
import IMenu from '@/js/components/molecules/IMenu.vue';
import IModal from '@/js/components/molecules/IModal.vue';
import ITextArea from '@/js/components/molecules/ITextArea.vue';
import ITextField from '@/js/components/molecules/ITextField.vue';
import QuestionIcon from '@/js/components/organisms/QuestionIcon.vue';

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    itemId?: number;
    shopId: number;
    subject?: string;
  }>(),
  {
    itemId: 0,
    subject: '',
  },
);
const userStore = useUserStore();
const isShowImage = ref(false);
const isShowMessageDialog = ref(false);
const subjectStr = ref(props.subject);
const messageStr = ref(props.itemId ? `\n\n${window.location.href.replace(window.location.search, '').replace(window.location.hash, '')}` : '');
const pictures = ref<Picture[]>([]);
const onClick = () => {
  if (userStore.isAuthenticated) {
    if (userStore.hasCredential(credentials.emailConfirmed)) {
      isShowMessageDialog.value = true;
    } else {
      info('メッセージを送信するにはメール認証が必要です。\n「マイページ > 設定 > ログイン情報 > メールアドレスの変更」よりメール認証を行ってください。');
    }
  } else {
    signinSignup({
      next: window.location.href,
      callback: () => {
        window.location.reload();
      },
    });
  }
};
const onSubmit = async () => {
  if (await confirm('メッセージを送信します。\nよろしいですか？')) {
    loading({
      asyncFunc: async () => {
        await messageSend({
          shopId: props.shopId,
          itemId: props.itemId,
          picture: pictures.value[0],
          subject: subjectStr.value,
          message: messageStr.value,
        });
        await info('メッセージを送信しました', { fadeOut: true });
        window.location.reload();
      },
    });
  }
};
</script>

<style module lang="scss">
.itemButton {
  font-size: 12px;
  transition: opacity 0.3s ease-in-out;
  width: 35px;

  @media (--sp) {
    font-size: 11px;
  }

  i {
    color: #4b4b4b;
    display: inline-block;
    font-size: 24px;
    margin: 5px 0 4px;
  }

  &:not(:disabled):hover {
    opacity: 0.8;
  }

  &:disabled {
    color: $gray500;

    i {
      color: $gray500;
    }
  }

  span {
    display: block;
    line-height: 1.4;
  }
}

.shopButton {
  border: 1px solid $gray300;
  border-radius: 50%;
  display: flex;
  font-size: 18px;
  padding: 7px 6px;

  &:not(:disabled):hover {
    opacity: 0.8;
  }

  i {
    color: #4b4b4b;
    position: relative;
    top: 1px;
  }

  &:disabled i {
    color: $gray500;
  }

  span {
    @media (--sp) {
      display: none;
    }
  }
}

.title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
}

.form {
  text-align: left;

  section {
    margin-bottom: 15px;

    .sectionHeader {
      align-items: center;
      display: flex;
      margin-bottom: 6px;

      > p {
        margin: 0 8px 0 0;
      }
    }
  }

  .attach {
    margin-bottom: 20px;

    button {
      border-bottom: 1px dashed $gray600;
      display: inline-block;

      &:hover {
        text-decoration: none;
      }

      &::selection {
        background: none;
      }
    }
  }

  .send {
    padding: 15px 0 0;
    text-align: center;
  }
}
</style>
